import { useCallback, useMemo, useState } from 'react';

export function useMultiSelect(items = [], getKey = (item) => item.id, initialSelected = []) {
    const [selectedIds, setSelectedIds] = useState(() => {
        if (initialSelected instanceof Set) return new Set(initialSelected);
        return new Set(initialSelected);
    });

    /** Check if an item ID is selected */
    const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

    /** Toggle selection of a single ID */
    const toggle = useCallback(
        (id) =>
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            }),
        [],
    );

    /** Select all items */
    const selectAll = useCallback(() => {
        const allKeys = items.map((item) => getKey(item));
        setSelectedIds(new Set(allKeys));
    }, [items, getKey]);

    /** Clear all selections */
    const clear = useCallback(() => setSelectedIds(new Set()), []);

    /** Replace entire selection */
    const setSelected = useCallback((ids) => {
        setSelectedIds(new Set(ids));
    }, []);

    /** Derived selected items */
    const selectedItems = useMemo(
        () => items.filter((item) => selectedIds.has(getKey(item))),
        [items, selectedIds, getKey],
    );

    return {
        selectedIds,
        selectedItems,
        isSelected,
        toggle,
        selectAll,
        clear,
        setSelected,
    };
}
